import { RootState } from '../index';
import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { returnCurrencySymbol } from '../../utils/utils';
import { selectCurrencyArray } from '../currency/selectors';
import { AD, roleRestricted, SA } from '../../components/RoleRestricted/utils';
import {
  populateInvestmentsAndComments,
  mapFinancialSummaryInfos,
  calculateStats,
  mapDirectiveToProjectFields,
} from './utils';
import {
  formatInvestments,
  formatToThousands,
  mapFinancialInfo,
  mapFinancialSummaryInfo,
} from '../utils/utils';
import getSymbolFromCurrency from 'currency-symbol-map';
import { returnCurrencyObject } from '../../hooks/useLocalCurrency';
import { enrichFinancialSummaryInfosWithEur } from '../approval/utils';

export const getProjects = (state: RootState) => state.projects.projects || [];

const getUserRole = (state: RootState) =>
  state.userData.userData.expansionUserRole;
export const bulkProjects = (state: RootState) =>
  state.projects.projectsForBulk;

export const selectProjectsWithFormattedInvestments = createDraftSafeSelector(
  getProjects,
  selectCurrencyArray,
  (projects, currencies) => {
    return projects.map((project) => {
      const financialInfo = mapFinancialInfo(project.financialInfo);
      const financialSummaryInfo = mapFinancialSummaryInfo(
        project.financialSummaryInfo,
      );
      const financialSummaryInfos = mapFinancialSummaryInfos(
        project,
        currencies,
      );
      return {
        ...project,
        ...financialInfo,
        ...financialSummaryInfo,
        ...financialSummaryInfos,
      };
    });
  },
);

export const selectAccessibleProjects = createDraftSafeSelector(
  [selectProjectsWithFormattedInvestments, getUserRole, selectCurrencyArray],
  (projects, userRole, currencies) =>
    projects
      .map((project) =>
        project?.isConfidential
          ? roleRestricted([AD, SA], userRole)
            ? project
            : undefined
          : project,
      )
      .filter((project) => project)
      .map((project) => {
        return {
          ...project,
          confidential: project.confidential || 'INTERNAL',
          solutionCategory: project?.solutions
            ?.map((solution) => solution.solutionCategory)
            .join(', '),
          solutionType: project?.solutions
            ?.map((solution) => solution.solutionType)
            .join(', '),
          solutionPurpose: project?.solutions
            ?.map((solution) => solution.solutionPurpose)
            .join(', '),
          projectManagerLeader: project?.projectResponsibleList?.find(
            (person) => person?.projectResponsibleRole === 'PROJECT_MANAGER',
          )?.projectResponsiblePerson,
          projectAdmin: project?.projectResponsibleList?.find(
            (person) => person?.projectResponsibleRole === 'PROJECT_ADMIN',
          )?.projectResponsiblePerson,
          projectOwner: project?.projectResponsibleList?.find(
            (person) => person?.projectResponsibleRole === 'PPROJECT_OWNER',
          )?.projectResponsiblePerson,
          projectOrderer: project?.projectResponsibleList?.find(
            (person) => person?.projectResponsibleRole === 'PROJECT_ORDERER',
          )?.projectResponsiblePerson,
          isLeasedFromAnotherIngkaCompany:
            project?.isLeasedFromAnotherIngkaCompany,
          alignedWithGEXFY: project?.fiscalYearAlignedWithGEXList?.reduce(
            (acc, fy) => {
              if (fy.baselineStartDate && fy.fiscalYear) {
                acc[fy.fiscalYear] = fy.alignedWithGEXType;
              }
              return acc;
            },
            {} as Record<string, string>,
          ),
          alignedWithGEXFYDates: project?.fiscalYearAlignedWithGEXList?.reduce(
            (acc, fy) => {
              if (fy.baselineStartDate && fy.fiscalYear) {
                acc[fy.fiscalYear] =
                  `${fy.alignedWithGEXType}: ${fy.baselineStartDate} - ${fy.baselineOpeningDate}`;
              }
              return acc;
            },
            {} as Record<string, string>,
          ),
          financialSummaryInfos: enrichFinancialSummaryInfosWithEur(
            project.financialSummaryInfos,
            currencies,
            project.countryCode,
          ),
          currencySymbol: getSymbolFromCurrency(
            returnCurrencyObject(
              currencies,
              project.countryCode,
              project.financialSummaryInfos?.[0]?.fiscalYear,
            )?.currencyCode || 'EUR',
          ),
          fyBudgetFYs: Object.entries(project.forecastSummary || {})
            .filter(([, value]) => value > 0)
            .map(([fy]) => fy),
        };
      })
      .map((project) => ({
        ...project,
        stats: calculateStats(project),
      })),
);

export const getLoading = (state: RootState) => state.projects.loading;
export const getSelectedProjectId = (state: RootState) =>
  state.projects.selectedProject;
export const selectProjectForApproval = createDraftSafeSelector(
  [getProjects, getSelectedProjectId],
  (projects, selectedProjectId) => {
    return projects.find((project) => project.id === selectedProjectId);
  },
);

export const projectSolutionSelector = (state: RootState) =>
  state.projects.projectToEdit?.solutions || [];

export const selectSolutions = (state: RootState) => state.projects.solutions;

export const selectSiteLocation = (state: RootState) =>
  state.projects.siteLocation;

export const selectProjectToEdit = (state: RootState) =>
  state.projects.projectToEdit;

export const formatFinancialsForSelectedProject = createDraftSafeSelector(
  selectProjectToEdit,
  (project) => {
    const financialSummaries = formatInvestments(project?.financialSummaries);
    const investments = formatInvestments(project?.investments);

    return {
      ...project,
      financialSummaries,
      investments,
      totalLeaseAmount: formatToThousands(project?.totalLeaseAmount),
      costAtLeaseEnd: formatToThousands(project?.costAtLeaseEnd),
    };
  },
);

export const selectProjectToEditWithFormattedInvestments =
  createDraftSafeSelector(formatFinancialsForSelectedProject, (project) => {
    return {
      ...populateInvestmentsAndComments(project),
      directive: project?.directive
        ? mapDirectiveToProjectFields(project.directive)
        : null,
    };
  });

export const selectProjectsCurrency = createDraftSafeSelector(
  [selectProjectToEdit, selectCurrencyArray],
  (project, currencies) => {
    if (!project || !currencies) {
      return '';
    }
    const currencyCode =
      returnCurrencyObject(
        currencies,
        project.countryCode,
        project.financialSummaryInfos?.[0]?.fiscalYear,
      )?.currencyCode || 'EUR';
    return returnCurrencySymbol(currencyCode);
  },
);

export const getProjectsForBulk = createDraftSafeSelector(
  [selectAccessibleProjects, bulkProjects],
  (projects, bulkProjects) => {
    return projects
      .filter((project) => bulkProjects.includes(project.id))
      .map((project) => {
        const financialSummaries = formatInvestments(
          project?.financialSummaries,
        );
        const investments = formatInvestments(project?.investments);
        return { ...project, financialSummaries, investments };
      })
      .map((project) => populateInvestmentsAndComments(project));
  },
);
